import type { MouseEventHandler } from 'react'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import CardCarousel from 'LuxKit/Carousel/CardCarousel'
import type { SelectionOption } from 'components/SearchV2/Components/FilterPanel/FilterPanel'
import { HolidayTypeToIconMap, getTopNFilterOptions, getHolidayTypeIcon } from 'lib/search/searchUtils'
import { connect } from 'react-redux'
import ToggleButton from 'LuxKit/ToggleButton'

import { useAppDispatch } from 'hooks/reduxHooks'
import { replace } from 'connected-react-router'
import { deleteSearchParams, setManySearchParamValues } from 'lib/url/searchUrlUtils'
import getOfferListKey from 'lib/offer/offerListKey'

const EMPTY_EXCLUDED_OPTIONS: Array<string> = []

interface Props {
  filters: App.OfferListFilters
  search: string
  excludedOptions?: Array<string>
}

interface MappedStateProps {
  availableFilters?: App.OfferListFilterOptions
}

function AnywhereFilterTabs(props: Props & MappedStateProps) {
  const intl = useIntl()

  // these are used in useCallback below so wrapping them in useMemo
  const sortFilters = useMemo<Record<string, string>>(() => ({
    [intl.formatMessage({
      id: '72mNRWquuD',
      defaultMessage: 'Best Value',
      description: 'Filter tab label for sorting by best value',
    })]: 'value',
  }), [intl])

  const amenityFilters = useMemo<Record<string, string>>(() => ({
    [intl.formatMessage({
      id: 'zA1K5oT3ex',
      defaultMessage: 'Private Pool',
      description: 'Filter tab label for private pool amenity',
    })]: 'Private pool',
    [intl.formatMessage({
      id: '3Qs23dp08v',
      defaultMessage: 'Ocean Views',
      description: 'Filter tab label for ocean views amenity',
    })]: 'Ocean views',
  }), [intl])

  const allLabel = intl.formatMessage({
    id: 'EtVu9WXeWv',
    defaultMessage: 'All',
    description: 'Filter tab label for showing all results',
  })

  const { filters, availableFilters, search, excludedOptions = EMPTY_EXCLUDED_OPTIONS } = props
  const [currentTab, setCurrentTab] = useState<string>(() => {
    if (filters.holidayTypes?.[0]) {
      return filters.holidayTypes?.[0]
    }
    if (filters.amenities?.[0] && Object.values(amenityFilters).includes(filters.amenities?.[0])) {
      return Object.keys(amenityFilters).find((key) => amenityFilters[key] === filters.amenities?.[0]) ?? allLabel
    }
    if (filters.sortBy && Object.values(sortFilters).includes(filters.sortBy)) {
      return Object.keys(sortFilters).find((key) => sortFilters[key] === filters.sortBy) ?? allLabel
    }
    return allLabel
  })
  const [availableFiltersInState, setAvailableFiltersInState] = useState<App.OfferListFilterOptions | null>(null)
  const initialOptionsOrder = useRef<Array<string> | null>(null)

  useEffect(() => {
    if (availableFilters !== undefined) {
      setAvailableFiltersInState(availableFilters)
    }
  }, [availableFilters])

  const dispatch = useAppDispatch()

  const handleSelect = useCallback<MouseEventHandler<HTMLButtonElement>>((event) => {
    const selectionId = String(event.currentTarget.dataset.selectionid)
    if (currentTab === selectionId || selectionId === allLabel) {
      setCurrentTab(allLabel)
      dispatch(
        replace({
          search: deleteSearchParams(
            search,
            'holidayTypes',
            'amenities',
            'sortBy',
            'limit',
          ),
        }),
      )
    } else if (Object.keys(sortFilters).includes(selectionId)) {
      setCurrentTab(selectionId)
      dispatch(
        replace({
          search: setManySearchParamValues(
            search,
            [
              { paramName: 'holidayTypes', paramValue: undefined },
              { paramName: 'amenities', paramValue: undefined },
              { paramName: 'sortBy', paramValue: sortFilters[selectionId] },
              { paramName: 'limit', paramValue: undefined },
            ],
          ),
        }),
      )
    } else if (Object.keys(amenityFilters).includes(selectionId)) {
      setCurrentTab(selectionId)
      dispatch(
        replace({
          search: setManySearchParamValues(
            search,
            [
              { paramName: 'holidayTypes', paramValue: undefined },
              { paramName: 'amenities', paramValue: amenityFilters[selectionId] },
              { paramName: 'sortBy', paramValue: undefined },
              { paramName: 'limit', paramValue: undefined },
            ],
          ),
        }),
      )
    } else {
      setCurrentTab(selectionId)
      dispatch(
        replace({
          search: setManySearchParamValues(
            search,
            [
              { paramName: 'holidayTypes', paramValue: selectionId },
              { paramName: 'amenities', paramValue: undefined },
              { paramName: 'sortBy', paramValue: undefined },
              { paramName: 'limit', paramValue: undefined },
            ],
          ),
        }),
      )
    }
  }, [currentTab, dispatch, search, allLabel, sortFilters, amenityFilters])

  const availableHolidayTypeFilters = availableFiltersInState?.filters?.holidayTypes

  const topHolidayTypes = getTopNFilterOptions(availableHolidayTypeFilters, availableFiltersInState?.filterOrder?.holidayTypes, 30)

  if (!topHolidayTypes) {
    return null
  }

  const customerFilters: Array<[string, number, number]> = [
    ...Object.keys(amenityFilters),
    ...Object.keys(sortFilters),
  ].map((filter) => ([filter, 100, 10]))

  const allFilters = [
    ...topHolidayTypes.slice(0, 6),
    ...customerFilters, // hardcode to the 7th position until we have tracking data
    ...topHolidayTypes.slice(6),
  ]

  const topHolidayTypeOptions = allFilters.map<SelectionOption>(
    ([holidayType]) => {
      return {
        icon: getHolidayTypeIcon(holidayType.toLowerCase()),
        text: holidayType,
        id: `${holidayType}-theme`,
      }
    },
  )

  const themeOptions: Array<SelectionOption> = [{
    icon: HolidayTypeToIconMap.All,
    text: allLabel,
    id: 'All-theme',
  }]
  const options = themeOptions.concat(topHolidayTypeOptions)
    .filter(option => !excludedOptions.includes(option.text))

  if (!initialOptionsOrder.current) {
    initialOptionsOrder.current = options.map(option => option.text)
  }
  const orderIndex = (option: SelectionOption, index: number) => {
    const orderedIndex = initialOptionsOrder.current?.indexOf(option.text) ?? -1
    return orderedIndex === -1 ? options.length + index : orderedIndex
  }
  const orderedOptions = [...options].sort((optionA, optionB) => (
    orderIndex(optionA, options.indexOf(optionA)) - orderIndex(optionB, options.indexOf(optionB))
  ))

  return (
    <CardCarousel gap={0}>
      {orderedOptions.map((option) => (
        <ToggleButton
          kind="primary"
          orientation="vertical"
          key={option.id}
          startIcon={option.icon}
          title={option.text}
          isSelected={currentTab === option.text}
          data-selectionid={option.text}
          onClick={handleSelect}
        />))}
    </CardCarousel>
  )
}

export default connect<MappedStateProps, undefined, Props, App.State>((appState, ownProps) => {
  const offerListKey = getOfferListKey(ownProps.filters)
  return {
    availableFilters: appState.offer.offerListFilterOptions[offerListKey],
  }
})(AnywhereFilterTabs)
